// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import TwoButtonModal from "@insite/content-library/Components/TwoButtonModal";
import DataTable from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Trash2Line from "@store/Library/Components/Toolkit/Icons/Trash2/Trash2Line";
import constants from "@store/constants";
import { getStoreListsDataView } from "@store/Framework/Store/Data/StoreLists/StoreListsSelector";
import deleteStoreList from "@store/Framework/Store/Pages/StoreLists/Handlers/DeleteStoreList";
import { StoreListModel } from "@store/Framework/Types/ApiModels";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as React from "react";
import { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    return {
        storeLists: getStoreListsDataView(state, state.store.pages.storeLists.getStoreListsParameter),
        language: state.context.session.language,
        storeListDetailsLink: getPageLinkByPageType(state, "StoreListDetailPage"),
    };
};

const mapDispatchToProps = {
    deleteStoreList,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface StoreListsListTableStyles {
    container?: InjectableCss;
    actionDeleteIcon?: IconPresentationProps;
    storeListDescriptionCell?: DataTableCellBaseProps;
}

export const tableStyles: StoreListsListTableStyles = {
    container: {
        css: css`
            overflow: auto;
            margin-top: ${pxToRem(5)};
        `,
    },
    actionDeleteIcon: {
        size: 22,
        css: css`
            color: ${getColor("icon")};
        `,
    },
    storeListDescriptionCell: {
        css: css`
            white-space: normal;
        `,
    },
};

const styles = tableStyles;

const StoreListsListTable = ({ storeLists, language, storeListDetailsLink, deleteStoreList }: Props) => {
    const toasterContext = useContext(ToasterContext);

    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
    const [storeListToAction, setStoreListToAction] = useState({
        name: "",
        description: "",
        createdOn: "",
    } as StoreListModel);

    const deleteButtonClickHandler = (storeList: StoreListModel) => {
        setDeleteModalIsOpen(true);
        setStoreListToAction(storeList);
    };

    const deleteCancelHandler = () => {
        setDeleteModalIsOpen(false);
    };

    const deleteSubmitHandler = () => {
        setDeleteModalIsOpen(false);
        if (storeListToAction) {
            deleteStoreList({
                storeListId: storeListToAction?.id,
                onSuccess: onDeleteSuccess,
            });
        }
    };

    const onDeleteSuccess = () => {
        toasterContext.addToast({ body: translate("Store List Deleted"), messageType: "success" });
    };

    if (!storeLists.value || storeLists.isLoading) {
        return (
            <StyledWrapper>
                <LoadingSpinner data-test-selector="storeLists_spinner"></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (storeLists.value?.length === 0) {
        return (
            <StyledWrapper>
                <Typography>{translate("No store list found.")}</Typography>
            </StyledWrapper>
        );
    }

    const storeListRows = storeLists.value?.map(storeList => {
        return {
            id: storeList.id,
            name: storeList.name,
            description: storeList.description,
            createdOn: getLocalizedDateTime({
                dateTime: new Date(storeList.createdOn),
                language,
            }),
            uri: storeList.uri,
            properties: storeList.properties,
        };
    });

    return (
        <StyledWrapper {...styles.container}>
            <DataTable>
                <DataTableHead>
                    <DataTableHeader></DataTableHeader>
                    <DataTableHeader>{translate("List Name")}</DataTableHeader>
                    <DataTableHeader>{translate("Description")}</DataTableHeader>
                    <DataTableHeader>{translate("Created On")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody>
                    {storeListRows?.map(storeList => (
                        <DataTableRow data-test-selector="storeList_listLine" key={storeList.name}>
                            <DataTableCell>
                                <Link
                                    onClick={(event: React.MouseEvent) => {
                                        deleteButtonClickHandler(storeList);
                                    }}
                                >
                                    <Icon {...styles.actionDeleteIcon} src={Trash2Line} />
                                </Link>
                            </DataTableCell>
                            <DataTableCell>
                                <Link href={`${storeListDetailsLink?.url}?listId=${storeList.id}`}>
                                    {storeList.name}
                                </Link>
                            </DataTableCell>
                            <DataTableCell {...styles.storeListDescriptionCell}>{storeList.description}</DataTableCell>
                            <DataTableCell>{storeList.createdOn}</DataTableCell>
                        </DataTableRow>
                    ))}
                </DataTableBody>
            </DataTable>
            <TwoButtonModal
                modalIsOpen={deleteModalIsOpen}
                headlineText={translate("Delete Store List")}
                messageText={`${translate("Are you sure you want to delete")} ${storeListToAction.name}?`}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                onCancel={deleteCancelHandler}
                onSubmit={deleteSubmitHandler}
                submitTestSelector="storeListSubmitDelete"
            ></TwoButtonModal>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(StoreListsListTable),
    definition: {
        group: constants.widgetGroup.storeLists,
        displayName: "Store List Table",
    },
};

export default widgetModule;
